import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { MdFavorite } from "react-icons/md";
import { MenuItem } from "../../redux/slices/menuSlice";
import { parsePrice, formatPrice } from "../../utils/priceUtils";

interface RecommendedProductsProps {
  currentProductId: string;
  allItems: MenuItem[];
  product: MenuItem;
  showPrice?: boolean;
  onItemClick?: (item: MenuItem) => void;
  forceHorizontal?: boolean; // Always show horizontal scroll (for modals)
}

const RecommendedProducts: React.FC<RecommendedProductsProps> = ({
  currentProductId,
  allItems,
  product,
  showPrice = true,
  onItemClick,
  forceHorizontal = false,
}) => {
  const recommendedItems = useMemo((): MenuItem[] => {
    if (!allItems?.length || !product.pairings?.length) return [];

    return allItems.filter((item) => {
      const isNotCurrentItem = item.id.toString() !== currentProductId;
      const matchesPairing = product.pairings.some((pairing: string) =>
        item.name.toLowerCase().includes(pairing.toLowerCase().trim())
      );
      return isNotCurrentItem && matchesPairing;
    });
  }, [allItems, product.pairings, currentProductId]);

  if (recommendedItems.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className="md:col-span-1"
    >
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <MdFavorite className="h-5 w-5 mr-2 text-red-500" />
        You May Also Like
      </h2>

      {/* Desktop - vertical list (hidden when forceHorizontal) */}
      {!forceHorizontal && (
        <div className="hidden md:block space-y-4">
          {recommendedItems.map((item) => (
            <motion.div
              key={item.id}
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <RecommendedItemCard
                item={item}
                showPrice={showPrice}
                onClick={() => onItemClick?.(item)}
              />
            </motion.div>
          ))}
        </div>
      )}

      {/* Horizontal scroll (always shown when forceHorizontal, otherwise mobile only) */}
      <div className={forceHorizontal ? "block" : "md:hidden"}>
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {recommendedItems.map((item) => (
            <motion.div
              key={item.id}
              whileTap={{ scale: 0.97 }}
              className="flex-shrink-0 w-32"
            >
              <RecommendedItemCard
                item={item}
                showPrice={showPrice}
                compact
                onClick={() => onItemClick?.(item)}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

interface RecommendedItemCardProps {
  item: MenuItem;
  showPrice?: boolean;
  compact?: boolean; // for mobile
  onClick?: () => void;
}

const RecommendedItemCard: React.FC<RecommendedItemCardProps> = React.memo(
  ({ item, showPrice = true, compact = false, onClick }) => {
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick?.();
      }
    };

    const renderImage = () => {
      if (item.image) {
        return (
          <img
            src={item.image}
            alt={item.name}
            className={
              compact
                ? "w-full h-20 object-cover rounded-md mb-2"
                : "w-16 h-16 object-cover rounded-md mr-3"
            }
          />
        );
      }

      return (
        <div
          className={`${
            compact ? "w-full h-20 mb-2" : "w-16 h-16 mr-3"
          } bg-red-100 flex items-center justify-center rounded-md`}
        >
          <span className="text-lg font-bold text-red-500">
            {item.name?.[0]?.toUpperCase() || "P"}
          </span>
        </div>
      );
    };

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md 
                   transition-shadow cursor-pointer hover:bg-gray-50 focus:outline-none 
                   focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
      >
        <div className={compact ? "p-2" : "flex items-center p-2"}>
          {renderImage()}

          <div className={compact ? "" : "flex-1"}>
            <h3
              className={`font-medium ${
                compact
                  ? "text-xs leading-tight mb-1 line-clamp-1"
                  : "text-sm line-clamp-1"
              }`}
            >
              {item.name}
            </h3>
            {showPrice && (
              <p className="text-red-500 text-xs font-semibold">
                {formatPrice(parsePrice(item.price))}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
);

RecommendedItemCard.displayName = "RecommendedItemCard";

export default RecommendedProducts;
